import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TrafficLight from "@/components/TrafficLight";
import CertModal from "@/components/CertModal";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Users, UserPlus, UserMinus, X, Search, Trash2 } from "lucide-react";

const STATUS_RANK = { expired: 0, expiring_soon: 1, current: 2, none: 3, unknown: 4 };

// Extract last name (last whitespace-separated token) for sorting.
const lastName = (n = "") => {
  const parts = String(n).trim().split(/\s+/);
  return (parts[parts.length - 1] || "").toLowerCase();
};

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("alpha");
  const [query, setQuery] = useState("");
  const [showAssign, setShowAssign] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [certEmployee, setCertEmployee] = useState(null);
  const [certTypes, setCertTypes] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.get("/meta/cert-types").then((r) => setCertTypes(r.data.types || [])); }, []);

  // keep cert modal in sync with reloaded job data
  useEffect(() => {
    if (!certEmployee || !job) return;
    const fresh = (job.employees || []).find((e) => e.id === certEmployee.id);
    if (fresh) setCertEmployee(fresh);
  }, [job, certEmployee]);

  const sorted = useMemo(() => {
    if (!job) return [];
    const q = query.trim().toLowerCase();
    let list = (job.employees || []).filter((e) =>
      !q || e.name.toLowerCase().includes(q) || e.trade.toLowerCase().includes(q)
    );
    if (sort === "alpha") list = [...list].sort((a, b) => lastName(a.name).localeCompare(lastName(b.name)) || a.name.localeCompare(b.name));
    if (sort === "trade") list = [...list].sort((a, b) => a.trade.localeCompare(b.trade) || lastName(a.name).localeCompare(lastName(b.name)));
    if (sort === "exp") list = [...list].sort((a, b) => (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9));
    return list;
  }, [job, sort, query]);

  const canAssign = user?.role === "admin" || user?.role === "supervisor";

  if (loading || !job) {
    return <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider">Loading...</div>;
  }

  return (
    <div className="p-4 md:p-10 max-w-7xl mx-auto">
      <Link to="/jobs" data-testid="back-to-jobs" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-6 hover:text-safety-yellow">
        <ArrowLeft className="w-4 h-4" /> All Jobs
      </Link>

      <div className="brutal-card p-5 md:p-8 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="inline-block bg-safety-yellow text-black px-2 py-0.5 font-bold uppercase tracking-widest text-[10px] mb-3">Job Site</div>
            <h1 className="font-heading text-4xl md:text-5xl">{job.name}</h1>
            <div className="text-sm font-bold uppercase tracking-wider text-muted-foreground mt-2 flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" />{job.location || "—"}</span>
              <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" />{(job.employees || []).length} assigned</span>
            </div>
          </div>
          {canAssign && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button data-testid="assign-btn" onClick={() => setShowAssign(true)} className="btn-primary">
                <UserPlus className="w-5 h-5 mr-2" strokeWidth={3} /> Assign Crew
              </button>
              <button data-testid="remove-crew-btn" onClick={() => setShowRemove(true)}
                className="inline-flex items-center justify-center bg-transparent text-status-expired font-black uppercase tracking-wider border-2 border-status-expired px-6 min-h-[56px] text-base transition-colors duration-150 hover:bg-status-expired hover:text-white">
                <UserMinus className="w-5 h-5 mr-2" strokeWidth={3} /> Remove Crew
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="brutal-card p-4 md:p-5 mb-5 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="job-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search crew..." className="industrial-input pl-11" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[{ k: "alpha", l: "Last Name" }, { k: "trade", l: "Trade" }, { k: "exp", l: "Expiration" }].map((s) => (
            <button key={s.k} data-testid={`jobsort-${s.k}`} onClick={() => setSort(s.k)}
              className={`h-14 px-4 border-2 font-bold uppercase tracking-wider text-xs transition-colors ${
                sort === s.k
                  ? "bg-safety-yellow text-black border-black dark:border-safety-yellow"
                  : "bg-transparent border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black"
              }`}>{s.l}</button>
          ))}
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="brutal-card p-10 text-center">
          <div className="font-heading text-2xl">No crew assigned yet</div>
          <div className="text-muted-foreground text-sm mt-2">Use &ldquo;Assign Crew&rdquo; to add employees to this job site.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="job-roster">
          {sorted.map((emp) => (
            <button key={emp.id} type="button" data-testid={`job-emp-${emp.id}`}
              onClick={() => setCertEmployee(emp)}
              className="brutal-card p-4 flex items-center gap-4 text-left w-full hover:bg-safety-yellow/10 transition-colors cursor-pointer">
              <div className="h-12 w-12 flex items-center justify-center bg-safety-yellow text-black font-heading text-xl border-2 border-black flex-shrink-0">
                {emp.name.split(" ").map(s => s[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-lg truncate">{emp.name}</div>
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{emp.trade}</div>
                {emp.soonest_expiration && (
                  <div className="text-[11px] mt-1 text-muted-foreground">
                    Soonest exp: <span className="font-bold text-foreground">{emp.soonest_expiration}</span>
                  </div>
                )}
              </div>
              <TrafficLight status={emp.status} size="sm" />
            </button>
          ))}
        </div>
      )}

      {showAssign && (
        <AssignModal
          jobId={id}
          currentIds={(job.employees || []).map(e => e.id)}
          onClose={() => setShowAssign(false)}
          onDone={() => { setShowAssign(false); load(); }}
        />
      )}

      {showRemove && (
        <RemoveCrewModal
          jobId={id}
          assigned={job.employees || []}
          onClose={() => setShowRemove(false)}
          onDone={() => { setShowRemove(false); load(); }}
        />
      )}

      {certEmployee && (
        <CertModal
          employee={certEmployee}
          certTypes={certTypes}
          userRole={user?.role}
          onClose={() => setCertEmployee(null)}
          onRefresh={load}
        />
      )}
    </div>
  );
}

function RemoveCrewModal({ jobId, assigned, onClose, onDone }) {
  useBodyScrollLock(true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const [removing, setRemoving] = useState(null);
  const removeOne = async (emp) => {
    if (!window.confirm(`Remove ${emp.name} from this job site?`)) return;
    setRemoving(emp.id);
    try {
      const remaining = assigned.filter((e) => e.id !== emp.id).map((e) => e.id);
      await api.post(`/jobs/${jobId}/assign`, { employee_ids: remaining });
      toast.success(`${emp.name} removed`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setRemoving(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}>
      <div className="bg-background brutal-card w-full md:max-w-xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b-2 border-foreground dark:border-safety-yellow p-4 sticky top-0 bg-background">
          <div>
            <div className="font-heading text-2xl">Remove Crew</div>
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-1">
              {assigned.length} currently assigned
            </div>
          </div>
          <button onClick={onClose} data-testid="remove-crew-close"
            className="h-12 w-12 border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black">
            <X className="w-5 h-5 mx-auto" strokeWidth={3} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {assigned.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground font-bold uppercase tracking-wider text-sm">
              No crew assigned to this job site.
            </div>
          ) : assigned.map((emp) => (
            <div key={emp.id} data-testid={`remove-row-${emp.id}`}
              className="border-2 border-foreground dark:border-safety-yellow p-3 flex items-center gap-3">
              <div className="h-10 w-10 flex-shrink-0 flex items-center justify-center bg-safety-yellow text-black font-heading text-lg border-2 border-black">
                {emp.name.split(" ").map(s => s[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-base truncate">{emp.name}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{emp.trade}</div>
              </div>
              <button data-testid={`remove-${emp.id}`} onClick={() => removeOne(emp)}
                disabled={removing === emp.id}
                className="min-h-[48px] px-3 border-2 border-status-expired text-status-expired hover:bg-status-expired hover:text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50">
                <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                {removing === emp.id ? "..." : "Remove"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AssignModal({ jobId, currentIds, onClose, onDone }) {
  useBodyScrollLock(true);
  const [all, setAll] = useState([]);
  const [selected, setSelected] = useState(new Set(currentIds));
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/employees").then(r => setAll(r.data));
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const save = async () => {
    try {
      await api.post(`/jobs/${jobId}/assign`, { employee_ids: Array.from(selected) });
      toast.success("Crew updated");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filtered = all.filter((e) => !q || e.name.toLowerCase().includes(q.toLowerCase()) || e.trade.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-0 md:p-6">
      <div className="bg-background brutal-card w-full md:max-w-xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b-2 border-foreground dark:border-safety-yellow p-4">
          <div className="font-heading text-2xl">Assign Crew · {selected.size}</div>
          <button onClick={onClose} data-testid="assign-close" className="h-10 w-10 border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black">
            <X className="w-5 h-5 mx-auto" strokeWidth={3} />
          </button>
        </div>
        <div className="p-4 border-b-2 border-dashed border-foreground/30 dark:border-safety-yellow/40">
          <input data-testid="assign-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search..." className="industrial-input" />
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {filtered.map((emp) => {
            const on = selected.has(emp.id);
            return (
              <button key={emp.id} data-testid={`assign-${emp.id}`} onClick={() => toggle(emp.id)}
                className={`w-full text-left border-2 p-3 flex items-center gap-3 transition-colors ${
                  on ? "bg-safety-yellow text-black border-black" : "border-foreground dark:border-safety-yellow hover:bg-safety-yellow/20"
                }`}>
                <div className={`h-6 w-6 border-2 ${on ? "bg-black border-black" : "border-foreground dark:border-safety-yellow"}`} />
                <div className="flex-1">
                  <div className="font-heading text-lg">{emp.name}</div>
                  <div className="text-[10px] font-bold uppercase tracking-widest">{emp.trade}</div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="p-4 border-t-2 border-foreground dark:border-safety-yellow">
          <button onClick={save} data-testid="assign-save" className="btn-primary w-full">Save Crew</button>
        </div>
      </div>
    </div>
  );
}
